import React, { useState, useEffect } from 'react';
import './DashboardAnimations.css';
import {
    BarChart2,
    PieChart,
    LineChart,
    AreaChart,
    Activity,
    Users,
    ArrowUp,
    ArrowDown,
    TrendingUp,
    Database,
    Zap,
    Eye,
    Clock,
    Filter,
    Download,
    Layers,
    AlertTriangle,
    Check,
    RefreshCw
} from 'lucide-react';
import DashboardMetricsSection from './DashboardMetricsSection';

const DataPipelineDashboard = () => {
    // Mock data for charts
    const [activeTab, setActiveTab] = useState('overview');
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Simulate data loading
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        setTimeout(() => {
            setRefreshing(false);
        }, 1000);
    };

    // Data flow animations
    const DataFlowLine = ({ color, delay }) => (
        <div className="absolute inset-0 overflow-hidden">
            {[...Array(10)].map((_, i) => (
                <div
                    key={i}
                    className={`absolute h-1 w-1 rounded-full bg-${color}-500 animate-flow-right`}
                    style={{
                        top: `${20 + Math.random() * 60}%`,
                        left: '-5%',
                        animationDelay: `${delay + i * 0.3}s`,
                        animationDuration: '3s'
                    }}
                ></div>
            ))}
        </div>
    );

    return (
        <div className="flex justify-center items-center p-4 bg-slate-950 min-h-screen">
            {/* Main container with max-width to prevent stretching */}
            <div className="w-full max-w-6xl mx-auto">
                <div className="relative bg-slate-900 rounded-xl shadow-2xl border border-slate-800 overflow-hidden">
                    {/* Pipeline-themed header with glowing accents */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500"></div>

                        {/* Pipeline decorative elements in header */}
                        <div className="absolute top-1/2 left-10 right-10 h-4 bg-slate-800 transform -translate-y-1/2 rounded-full border-t border-b border-slate-600/30">
                            {[...Array(15)].map((_, i) => (
                                <div
                                    key={i}
                                    className="absolute h-full w-1 border-l border-r border-slate-600/30"
                                    style={{ left: `${i * 7}%` }}
                                ></div>
                            ))}

                            {/* Flowing data animation */}
                            <DataFlowLine color="blue" delay={0} />
                            <DataFlowLine color="purple" delay={0.5} />
                            <DataFlowLine color="emerald" delay={1} />
                        </div>

                        <div className="flex items-center justify-between px-6 py-4 relative z-10">
                            <div className="flex items-center space-x-4">
                                <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-2 rounded-lg">
                                    <Database className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Data Pipeline Dashboard</h1>
                                    <p className="text-slate-400 text-sm">Real-time performance insights</p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button onClick={handleRefresh} className="flex items-center space-x-1 text-slate-300 hover:text-white">
                                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                    <span className="text-sm">Refresh</span>
                                </button>

                                <div className="flex items-center space-x-1 px-3 py-1 bg-green-500/10 rounded-full border border-green-500/20">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                    <span className="text-green-400 text-xs font-medium">Live</span>
                                </div>

                                <div className="hidden md:flex items-center space-x-1 text-slate-400">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">Last updated: <span className="text-slate-300">Just now</span></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dashboard tabs */}
                    <div className="flex items-center px-6 py-2 bg-slate-800/50 border-b border-slate-700/50 overflow-x-auto">
                        <div className="flex space-x-1">
                            {['overview', 'performance', 'insights', 'alerts'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-t-lg text-sm font-medium capitalize transition-colors ${activeTab === tab
                                        ? 'bg-slate-800 text-white border-t border-l border-r border-slate-600/50'
                                        : 'text-slate-400 hover:text-slate-300'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className="ml-auto flex items-center space-x-2">
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Filter className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Download className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Main dashboard content */}
                    <div className="p-6">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-96">
                                <div className="relative">
                                    <Database className="w-12 h-12 text-slate-600 animate-pulse" />
                                    <div className="absolute w-full h-full top-0 left-0 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Top metrics cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                    {[
                                        {
                                            title: 'Pipeline Throughput',
                                            value: '98.2%',
                                            trend: 'up',
                                            change: '2.1%',
                                            icon: <Activity className="w-5 h-5" />,
                                            color: 'blue'
                                        },
                                        {
                                            title: 'Data Quality Score',
                                            value: '94.7%',
                                            trend: 'up',
                                            change: '1.3%',
                                            icon: <Check className="w-5 h-5" />,
                                            color: 'emerald'
                                        },
                                        {
                                            title: 'Processing Time',
                                            value: '124ms',
                                            trend: 'down',
                                            change: '12ms',
                                            icon: <Zap className="w-5 h-5" />,
                                            color: 'amber'
                                        },
                                        {
                                            title: 'Active Users',
                                            value: '2,847',
                                            trend: 'up',
                                            change: '257',
                                            icon: <Users className="w-5 h-5" />,
                                            color: 'purple'
                                        }
                                    ].map((metric, index) => (
                                        <div
                                            key={index}
                                            className="relative bg-slate-800 rounded-xl p-4 border border-slate-700 overflow-hidden"
                                        >
                                            {/* Decorative accent */}
                                            <div className={`absolute top-0 left-0 w-full h-1 bg-${metric.color}-500`}></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className={`p-2 rounded-lg bg-${metric.color}-500/10 text-${metric.color}-500`}>
                                                        {metric.icon}
                                                    </div>
                                                    <div className={`flex items-center text-${metric.trend === 'up' ? 'green' : 'red'}-400 text-sm`}>
                                                        {metric.trend === 'up' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                                                        {metric.change}
                                                    </div>
                                                </div>

                                                <div className="mb-1 text-slate-400 text-sm">{metric.title}</div>
                                                <div className="text-2xl font-bold text-white">{metric.value}</div>
                                            </div>

                                            {/* Decorative gradient */}
                                            <div className={`absolute bottom-0 right-0 w-24 h-24 bg-${metric.color}-500/5 rounded-full blur-2xl -mb-8 -mr-8`}></div>
                                        </div>
                                    ))}
                                </div>

                                {/* Main charts grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                                    {/* Large chart - Pipeline Performance */}
                                    <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                                            <div>
                                                <h3 className="font-medium text-white">Pipeline Performance</h3>
                                                <p className="text-slate-400 text-sm">Throughput and latency metrics</p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button className="px-3 py-1 text-xs text-slate-300 bg-slate-700 rounded-full hover:bg-slate-600">Day</button>
                                                <button className="px-3 py-1 text-xs text-white bg-blue-500 rounded-full">Week</button>
                                                <button className="px-3 py-1 text-xs text-slate-300 bg-slate-700 rounded-full hover:bg-slate-600">Month</button>
                                            </div>
                                        </div>

                                        <div className="px-4 py-4">
                                            {/* Chart visualization - this would be replaced with an actual chart component */}
                                            <div className="h-72 w-full relative overflow-hidden">
                                                {/* Simulated chart visualization */}
                                                <div className="absolute bottom-0 left-0 right-0 h-64 flex items-end">
                                                    {/* X-axis */}
                                                    <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-700"></div>

                                                    {/* Y-axis */}
                                                    <div className="absolute top-0 bottom-0 left-0 w-px bg-slate-700"></div>

                                                    {/* Y-axis labels */}
                                                    {[0, 25, 50, 75, 100].map((value, i) => (
                                                        <div key={i} className="absolute left-0 text-xs text-slate-500" style={{ bottom: `${value * 0.64}px` }}>
                                                            <span className="block w-6 text-right mr-2">{value}%</span>
                                                            <div className="absolute left-8 right-0 h-px bg-slate-700/30" style={{ opacity: value === 0 ? 0 : 1 }}></div>
                                                        </div>
                                                    ))}

                                                    {/* Simulated line chart for throughput */}
                                                    <div className="relative flex-1 flex items-end">
                                                        <div className="relative w-full h-full">
                                                            <svg className="w-full h-full" viewBox="0 0 100 64" preserveAspectRatio="none">
                                                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                    <stop offset="0%" stopColor="rgba(59, 130, 246, 0.5)" />
                                                                    <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
                                                                </linearGradient>
                                                                <path
                                                                    d="M0 50 L10 45 L20 55 L30 40 L40 48 L50 35 L60 40 L70 30 L80 25 L90 15 L100 10 V64 H0 Z"
                                                                    fill="url(#gradient1)"
                                                                    className="animate-fade-in"
                                                                    style={{ animationDelay: '0.5s' }}
                                                                />
                                                                <path
                                                                    d="M0 50 L10 45 L20 55 L30 40 L40 48 L50 35 L60 40 L70 30 L80 25 L90 15 L100 10"
                                                                    fill="none"
                                                                    stroke="#3B82F6"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="animate-draw-line"
                                                                    style={{ animationDuration: '2s' }}
                                                                />
                                                            </svg>
                                                        </div>
                                                    </div>

                                                    {/* X-axis labels */}
                                                    <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-slate-500 px-4 pb-4">
                                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                                                            <div key={i} className="text-center">
                                                                <div className="absolute bottom-0 w-px h-2 bg-slate-700" style={{ left: `${(i + 0.5) * 14.28}%` }}></div>
                                                                <span>{day}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Animated data points */}
                                                <div className="absolute bottom-0 left-0 right-0 h-64">
                                                    <div className="absolute w-3 h-3 bg-blue-500 rounded-full shadow-lg shadow-blue-500/30 animate-pulse" style={{ bottom: '40%', left: '30%' }}></div>
                                                    <div className="absolute w-3 h-3 bg-blue-500 rounded-full shadow-lg shadow-blue-500/30 animate-pulse" style={{ bottom: '25%', left: '70%', animationDelay: '0.5s' }}></div>
                                                    <div className="absolute w-3 h-3 bg-blue-500 rounded-full shadow-lg shadow-blue-500/30 animate-pulse" style={{ bottom: '10%', left: '90%', animationDelay: '1s' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Sources Breakdown */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Data Sources</h3>
                                            <p className="text-slate-400 text-sm">Volume processed by source</p>
                                        </div>

                                        <div className="px-4 py-4">
                                            {/* Donut chart visualization - would be replaced with actual chart */}
                                            <div className="h-64 w-full flex items-center justify-center">
                                                <div className="relative w-40 h-40">
                                                    {/* SVG Pie Chart */}
                                                    <svg viewBox="0 0 100 100" className="w-full h-full">
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#4F46E5" strokeWidth="20" strokeDasharray="251.2" strokeDashoffset="0" className="animate-chart-fill" style={{ animationDelay: '0s' }} />
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#EC4899" strokeWidth="20" strokeDasharray="251.2" strokeDashoffset="175.84" className="animate-chart-fill" style={{ animationDelay: '0.2s' }} />
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#10B981" strokeWidth="20" strokeDasharray="251.2" strokeDashoffset="213.52" className="animate-chart-fill" style={{ animationDelay: '0.4s' }} />
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#F59E0B" strokeWidth="20" strokeDasharray="251.2" strokeDashoffset="238.64" className="animate-chart-fill" style={{ animationDelay: '0.6s' }} />
                                                        <circle cx="50" cy="50" r="20" fill="#1E293B" />
                                                    </svg>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-white">2.4TB</div>
                                                            <div className="text-xs text-slate-400">Total Data</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Legend */}
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                {[
                                                    { color: 'indigo', label: 'CRM Data', value: '42%' },
                                                    { color: 'pink', label: 'Analytics', value: '30%' },
                                                    { color: 'emerald', label: 'IoT Sensors', value: '15%' },
                                                    { color: 'amber', label: 'Other', value: '13%' }
                                                ].map((item, i) => (
                                                    <div key={i} className="flex items-center">
                                                        <div className={`w-3 h-3 rounded-full bg-${item.color}-500 mr-2`}></div>
                                                        <div className="text-sm text-slate-300">{item.label}</div>
                                                        <div className="text-sm font-medium text-white ml-auto">{item.value}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom row - three equal cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Processing Status */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Jobs Status</h3>
                                            <p className="text-slate-400 text-sm">Real-time processing</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-4">
                                                {[
                                                    { name: 'Data Ingestion', status: 'running', progress: 75, color: 'blue' },
                                                    { name: 'Transformation', status: 'queued', progress: 0, color: 'amber' },
                                                    { name: 'ML Processing', status: 'completed', progress: 100, color: 'emerald' },
                                                    { name: 'Data Export', status: 'failed', progress: 45, color: 'red' }
                                                ].map((job, i) => (
                                                    <div key={i} className="relative">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="text-sm font-medium text-slate-300">{job.name}</div>
                                                            <div className={`text-xs px-2 py-0.5 rounded-full ${job.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                                                                job.status === 'queued' ? 'bg-amber-500/20 text-amber-400' :
                                                                    job.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                                                        'bg-red-500/20 text-red-400'
                                                                }`}>
                                                                {job.status}
                                                            </div>
                                                        </div>
                                                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full bg-${job.color}-500 rounded-full`}
                                                                style={{ width: `${job.progress}%`, transition: 'width 1s' }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Alerts */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Recent Alerts</h3>
                                            <p className="text-slate-400 text-sm">System notifications</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    {
                                                        type: 'error',
                                                        message: 'Pipeline timeout: Export process',
                                                        time: '12m ago',
                                                        icon: <AlertTriangle className="w-4 h-4 text-red-500" />
                                                    },
                                                    {
                                                        type: 'warning',
                                                        message: 'Unusual data volume in CRM feed',
                                                        time: '37m ago',
                                                        icon: <AlertTriangle className="w-4 h-4 text-amber-500" />
                                                    },
                                                    {
                                                        type: 'info',
                                                        message: 'Scheduled maintenance upcoming',
                                                        time: '2h ago',
                                                        icon: <Eye className="w-4 h-4 text-blue-500" />
                                                    },
                                                    {
                                                        type: 'success',
                                                        message: 'ML model retrained successfully',
                                                        time: '3h ago',
                                                        icon: <Check className="w-4 h-4 text-emerald-500" />
                                                    },
                                                    {
                                                        type: 'info',
                                                        message: 'New data source connected: IoT Hub',
                                                        time: '5h ago',
                                                        icon: <Database className="w-4 h-4 text-blue-500" />
                                                    }
                                                ].map((alert, i) => (
                                                    <div key={i} className="flex items-start p-2 rounded-lg hover:bg-slate-700/30">
                                                        <div className={`p-2 rounded-full bg-${alert.type === 'error' ? 'red' :
                                                            alert.type === 'warning' ? 'amber' :
                                                                alert.type === 'success' ? 'emerald' : 'blue'
                                                            }-500/10 mr-3`}>
                                                            {alert.icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-slate-300">{alert.message}</p>
                                                            <p className="text-xs text-slate-500">{alert.time}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Upcoming Schedule */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Pipeline Schedule</h3>
                                            <p className="text-slate-400 text-sm">Upcoming operations</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    {
                                                        name: 'Data warehouse update',
                                                        time: '04:30 AM',
                                                        status: 'scheduled',
                                                        icon: <Database className="w-4 h-4" />
                                                    },
                                                    {
                                                        name: 'ML model retraining',
                                                        time: '09:00 AM',
                                                        status: 'scheduled',
                                                        icon: <RefreshCw className="w-4 h-4" />
                                                    },
                                                    {
                                                        name: 'Business metrics sync',
                                                        time: '12:00 PM',
                                                        status: 'running',
                                                        icon: <BarChart2 className="w-4 h-4" />
                                                    },
                                                    {
                                                        name: 'Customer analytics',
                                                        time: '03:00 PM',
                                                        status: 'scheduled',
                                                        icon: <Users className="w-4 h-4" />
                                                    },
                                                    {
                                                        name: 'Backup procedure',
                                                        time: '11:00 PM',
                                                        status: 'scheduled',
                                                        icon: <Layers className="w-4 h-4" />
                                                    }
                                                ].map((schedule, i) => (
                                                    <div key={i} className="flex items-center p-2 rounded-lg hover:bg-slate-700/30">
                                                        <div className="p-2 bg-slate-700 rounded-lg mr-3 text-slate-300">
                                                            {schedule.icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-slate-300">{schedule.name}</p>
                                                            <p className="text-xs text-slate-500">{schedule.time}</p>
                                                        </div>
                                                        <div className={`text-xs px-2 py-0.5 rounded-full ${schedule.status === 'running' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600 text-slate-300'
                                                            }`}>
                                                            {schedule.status}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer with pipeline visualization */}
                    <div className="relative px-4 py-3 border-t border-slate-700 bg-slate-800/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <span className="text-xs text-slate-400">4 pipelines active</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                                    <span className="text-xs text-slate-400">1 pipeline pending</span>
                                </div>
                                <div className="hidden sm:flex items-center space-x-2">
                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                    <span className="text-xs text-slate-400">2.4 TB processed today</span>
                                </div>
                            </div>

                            <div className="hidden md:block text-xs text-slate-500">
                                <span>DIO Pipeline Platform v2.5.1</span>
                            </div>
                        </div>
                    </div>

                    {/* Floating pipeline nodes */}
                    <div className="absolute bottom-20 right-6">
                        <div className="relative w-16 h-16">
                            <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
                            <div className="relative w-full h-full rounded-full bg-slate-800 border border-blue-500 flex items-center justify-center">
                                <Database className="w-6 h-6 text-blue-500" />
                            </div>
                        </div>
                    </div>

                    {/* Pipeline connection - decorative */}
                    <div className="absolute bottom-0 right-0 left-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500"></div>
                </div>
            </div>
        </div>
    );
};

export default DataPipelineDashboard;